/**
 * A basic Web GL class. This provides a very basic setup for GLSL shader code.
 */
class WTCGL {

    /**
     * The WTCGL Class constructor.
     */
    constructor(el, vertexShaderSource, fragmentShaderSource, width, height, pxratio, styleElement, webgl2, params = {}) {
        this.run = this.run.bind(this);

        this._onRun = () => {
        };

        // Destructure if an object is aprovided instead a series of parameters
        if (el instanceof Object && el.el) {
            ({el, vertexShaderSource, fragmentShaderSource, width, height, pxratio, webgl2, styleElement} = el);
        }

        // If the HTML element isn't a canvas, return null
        if (!(el instanceof HTMLElement) || el.nodeName.toLowerCase() !== 'canvas') {
            console.log('Provided element should be a canvas element');
            return null;
        }

        this._el = el;
        // The context should be either webgl2, webgl or experimental-webgl
        if (webgl2 === true) {
            this.isWebgl2 = true;
            this._ctx = this._el.getContext("webgl2", this.webgl_params) || this._el.getContext("webgl", this.webgl_params) || this._el.getContext("experimental-webgl", this.webgl_params);
        } else {
            this.isWebgl2 = false;
            this._ctx = this._el.getContext("webgl", Object.assign({}, this.webgl_params, params)) || this._el.getContext("experimental-webgl", Object.assign({}, this.webgl_params, params));
        }

        // Set up the extensions
        this._ctx.getExtension('OES_standard_derivatives');
        this._ctx.getExtension('EXT_shader_texture_lod');
        this._ctx.getExtension('OES_texture_float');
        this._ctx.getExtension('WEBGL_color_buffer_float');
        this._ctx.getExtension('OES_texture_float_linear');
        this._ctx.getExtension('EXT_color_buffer_float');

        // We can't make the context so return an error
        if (!this._ctx) {
            console.log('Browser doesn\'t support WebGL ');
            return null;
        }

        // Create the shaders
        this._vertexShader = WTCGL.createShaderOfType(this._ctx, this._ctx.VERTEX_SHADER, vertexShaderSource);
        this._fragmentShader = WTCGL.createShaderOfType(this._ctx, this._ctx.FRAGMENT_SHADER, fragmentShaderSource);

        // Create the program and link the shaders
        this._program = this._ctx.createProgram();
        this._ctx.attachShader(this._program, this._vertexShader);
        this._ctx.attachShader(this._program, this._fragmentShader);
        this._ctx.linkProgram(this._program);

        // If we can't set up the params, this means the shaders have failed for some reason
        if (!this._ctx.getProgramParameter(this._program, this._ctx.LINK_STATUS)) {
            console.log('Unable to initialize the shader program: ' + this._ctx.getProgramInfoLog(this._program));
            return null;
        }

        // Initialise the vertex buffers
        this.initBuffers([
            -1.0, 1.0, -1.,
            1.0, 1.0, -1.,
            -1.0, -1.0, -1.,
            1.0, -1.0, -1.]);


        // Initialise the frame buffers
        this.frameBuffers = [];

        // The program information object. This is essentially a state machine for the webGL instance
        this._programInfo = {
            attribs: {
                vertexPosition: this._ctx.getAttribLocation(this._program, 'a_position')
            },

            uniforms: {
                projectionMatrix: this._ctx.getUniformLocation(this._program, 'u_projectionMatrix'),
                modelViewMatrix: this._ctx.getUniformLocation(this._program, 'u_modelViewMatrix'),
                resolution: this._ctx.getUniformLocation(this._program, 'u_resolution'),
                time: this._ctx.getUniformLocation(this._program, 'u_time')
            }
        };


        // Tell WebGL to use our program when drawing
        this._ctx.useProgram(this._program);

        this.pxratio = pxratio;

        this.styleElement = styleElement !== true;

        this.resize(width, height);
    }


    /**
     * Public methods
     */

    addFrameBuffer(w, h, tiling = 0, buffertype = 0) {
        // create to render to
        const gl = this._ctx;
        const targetTextureWidth = w * this.pxratio;
        const targetTextureHeight = h * this.pxratio;
        const targetTexture = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, targetTexture);
        {
            // define size and format of level 0
            const level = 0;
            let internalFormat = gl.RGBA;
            const border = 0;
            let format = gl.RGBA;
            let t;
            if (buffertype & WTCGL.TEXTYPE_FLOAT) {
                const e = gl.getExtension('OES_texture_float');
                window.extension = e;
                t = e.FLOAT;
                // internalFormat = gl.RGBA32F;
            } else if (buffertype & WTCGL.TEXTYPE_HALF_FLOAT_OES) {
                const e = gl.getExtension('OES_texture_half_float');
                t = this.isWebgl2 ? gl.HALF_FLOAT : e.HALF_FLOAT_OES;
                if (this.isWebgl2) {
                    internalFormat = gl.RGBA16F;
                }
            } else {
                t = gl.UNSIGNED_BYTE;
            }
            const type = t;
            const data = null;
            gl.texImage2D(gl.TEXTURE_2D, level, internalFormat,
                targetTextureWidth, targetTextureHeight, border,
                format, type, data);

            // set the filtering so we don't need mips
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);

            // Set the parameters based on the passed type
            if (tiling === WTCGL.IMAGETYPE_TILE) {
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
            } else if (tiling === WTCGL.IMAGETYPE_MIRROR) {
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.MIRRORED_REPEAT);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.MIRRORED_REPEAT);
            } else if (tiling === WTCGL.IMAGETYPE_REGULAR) {
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
            }
        }

        // Create and bind the framebuffer
        const fb = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, fb);

        // attach the texture as the first color attachment
        const attachmentPoint = gl.COLOR_ATTACHMENT0;
        const level = 0;
        gl.framebufferTexture2D(gl.FRAMEBUFFER, attachmentPoint, gl.TEXTURE_2D, targetTexture, level);

        return {
            w: w * this.pxratio,
            h: h * this.pxratio,
            fb: fb,
            frameTexture: targetTexture
        };

    }


    /**
     * Resizes the canvas to a specified width and height, respecting the pixel ratio
     */
    resize(w, h) {
        this.width = w;
        this.height = h;
        this._el.width = w * this.pxratio;
        this._el.height = h * this.pxratio;
        this._size = [w * this.pxratio, h * this.pxratio];
        if (this.styleElement) {
            this._el.style.width = w + 'px';
            this._el.style.height = h + 'px';
        }

        this._ctx.viewportWidth = w * this.pxratio;
        this._ctx.viewportHeight = h * this.pxratio;

        this._ctx.uniform2fv(this._programInfo.uniforms.resolution, this._size);

        this.initBuffers(this._positions);
    }

    /**
     * Initialise a provided vertex buffer
     */
    initBuffers(positions) {
        this._positions = positions;
        this._positionBuffer = this._ctx.createBuffer();

        this._ctx.bindBuffer(this._ctx.ARRAY_BUFFER, this._positionBuffer);

        this._ctx.bufferData(this._ctx.ARRAY_BUFFER,
            new Float32Array(positions),
            this._ctx.STATIC_DRAW);
    }

    /**
     * Add a uniform to the program.
     */
    addUniform(name, type, value) {
        this._uniformValues = this._uniformValues || {};
        this._uniformValues[name] = value;

        let uniform = this._programInfo.uniforms[name];
        if (!uniform) {
            uniform = this._ctx.getUniformLocation(this._program, `u_${name}`);
            this._programInfo.uniforms[name] = uniform;
        }
        if (!uniform) return null;
        switch (type) {
            case WTCGL.TYPE_INT:
                if (!isNaN(value)) this._ctx.uniform1i(uniform, value);
                break;
            case WTCGL.TYPE_FLOAT:
                if (!isNaN(value)) this._ctx.uniform1f(uniform, value);
                break;
            case WTCGL.TYPE_V2:
                if (value instanceof Array && value.length === 2.) this._ctx.uniform2fv(uniform, value);
                break;
            case WTCGL.TYPE_V3:
                if (value instanceof Array && value.length === 3.) this._ctx.uniform3fv(uniform, value);
                break;
            case WTCGL.TYPE_V4:
                if (value instanceof Array && value.length === 4.) this._ctx.uniform4fv(uniform, value);
                break;
            case WTCGL.TYPE_BOOL:
                if (!isNaN(value)) this._ctx.uniform1i(uniform, value);
                break;
        }

        this._programInfo.uniforms[name] = uniform;
        return uniform;
    }

    /**
     * Get a cached uniform value by logical name (without the `u_` prefix).
     */
    getUniform(name) {
        return this._uniformValues ? this._uniformValues[name] : null;
    }

    /**
     * Adds a texture to the program and links it to a named uniform.
     */
    addTexture(name, type, image, liveUpdate = false) {

        var texture = this._ctx.createTexture();
        this._ctx.pixelStorei(this._ctx.UNPACK_FLIP_Y_WEBGL, true);
        this._ctx.bindTexture(this._ctx.TEXTURE_2D, texture);

        // Set the parameters based on the passed type
        if (type === WTCGL.IMAGETYPE_MIRROR) {
            this._ctx.texParameteri(this._ctx.TEXTURE_2D, this._ctx.TEXTURE_WRAP_S, this._ctx.MIRRORED_REPEAT);
            this._ctx.texParameteri(this._ctx.TEXTURE_2D, this._ctx.TEXTURE_WRAP_T, this._ctx.MIRRORED_REPEAT);
        } else if (type === WTCGL.IMAGETYPE_REGULAR) {
            this._ctx.texParameteri(this._ctx.TEXTURE_2D, this._ctx.TEXTURE_WRAP_S, this._ctx.CLAMP_TO_EDGE);
            this._ctx.texParameteri(this._ctx.TEXTURE_2D, this._ctx.TEXTURE_WRAP_T, this._ctx.CLAMP_TO_EDGE);
        }

        this._ctx.texParameteri(this._ctx.TEXTURE_2D, this._ctx.TEXTURE_MIN_FILTER, this._ctx.LINEAR);

        // Upload the image into the texture.
        this._ctx.texImage2D(this._ctx.TEXTURE_2D, 0, this._ctx.RGBA, this._ctx.RGBA, this._ctx.UNSIGNED_BYTE, image);

        // add the texture to the array of textures.
        this.pushTexture(name, texture, image, this._ctx.TEXTURE_2D, liveUpdate);


        return texture;
    }

    pushTexture(name, texture, image, target, liveUpdate = false) {
        let textures = this.textures;

        textures.push({name: name, tex: texture, liveUpdate: liveUpdate, image: image, target: target});

        // Finally set the this.textures (this is just to get around the funnyness of default getters)
        this.textures = textures;
    }

    /**
     * Updates a texture location for a given WebGLTexture with an image
     */
    updateTexture(texture, image, name) {

        let uniform = this._ctx.getUniformLocation(this._program, `u_${name}`);
        // Set the texture unit to the uniform
        this._ctx.uniform1i(uniform, 0);
        this._ctx.activeTexture(this._ctx.TEXTURE0);

        this._ctx.bindTexture(this._ctx.TEXTURE_2D, texture);
        // Upload the image into the texture.
        this._ctx.texImage2D(this._ctx.TEXTURE_2D, 0, this._ctx.RGBA, this._ctx.RGBA, this._ctx.UNSIGNED_BYTE, image);
    }

    /**
     * Initialise texture locations in the program
     */
    initTextures() {
        for (let i = 0; i < this.textures.length; i++) {
            let name = this.textures[i].name;
            let uniform = this._programInfo.uniforms[name];
            uniform = this._ctx.getUniformLocation(this._program, `u_${name}`);

            // Set the texture unit to the uniform
            this._ctx.uniform1i(uniform, i);

            // find the active texture based on the index
            this._ctx.activeTexture(this._ctx[`TEXTURE${i}`]);

            // Finally, bind the texture
            this._ctx.bindTexture(this.textures[i].target, this.textures[i].tex);
        }
    }

    /**
     * The run loop.
     */
    run(delta) {
        this.running && requestAnimationFrame(this.run);

        const runFunction = () => {
            this.time = this.startTime + delta * .0002;
            this.onRun(delta);
            this.render();
        };

        if (this.frameRate) {
            let now = Date.now();
            let elapsed = now - this._then;

            if (elapsed > this.frameRate) {
                this._then = now - elapsed % this.frameRate;

                runFunction();
            }
        } else {
            runFunction();
        }
    }

    /**
     * Render the program
     */
    render(buffer = {}) {
        this._ctx.bindFramebuffer(this._ctx.FRAMEBUFFER, buffer.fb || null);
        // Update the time uniform
        this._ctx.uniform1f(this._programInfo.uniforms.time, this.time);

        this.textures.forEach(textureInfo => {
            if (textureInfo.liveUpdate === true) {
                this.updateTexture(textureInfo.tex, textureInfo.image, textureInfo.name);
            }
        });

        this._ctx.viewport(0, 0, buffer.w || this._ctx.viewportWidth, buffer.h || this._ctx.viewportHeight);
        if (this.clearing) {
            this._ctx.clearColor(0.0, 0.0, 0.0, 0.0);

            this._ctx.blendFunc(this._ctx.SRC_ALPHA, this._ctx.ONE_MINUS_SRC_ALPHA);
            this._ctx.enable(this._ctx.BLEND);

            this._ctx.clear(this._ctx.COLOR_BUFFER_BIT);
        }

        this._ctx.bindBuffer(this._ctx.ARRAY_BUFFER, this._positionBuffer);
        this._ctx.vertexAttribPointer(
            this._programInfo.attribs.vertexPosition,
            3,
            this._ctx.FLOAT,
            false,
            0,
            0);
        this._ctx.enableVertexAttribArray(this._programInfo.attribs.vertexPosition);

        // Set the shader uniforms
        this.includePerspectiveMatrix && this._ctx.uniformMatrix4fv(this._programInfo.uniforms.projectionMatrix, false, this.perspectiveMatrix);
        this.includeModelViewMatrix && this._ctx.uniformMatrix4fv(this._programInfo.uniforms.modelViewMatrix, false, this.modelViewMatrix);

        this._ctx.drawArrays(this._ctx.TRIANGLE_STRIP, 0, 4);
    }


    /**
     * Getters and setters
     */

    get webgl_params() {
        return {alpha: true};
    }

    set styleElement(value) {
        this._styleElement = value === true;
        if (this._styleElement === false && this._el) {
            this._el.style.width = '';
            this._el.style.height = '';
        }
    }

    get styleElement() {
        return this._styleElement !== false;
    }

    set startTime(value) {
        if (!isNaN(value)) {
            this._startTime = value;
        }
    }

    get startTime() {
        return this._startTime || 0;
    }

    set time(value) {
        if (!isNaN(value)) {
            this._time = value;
        }
    }

    get time() {
        return this._time || 0;
    }

    set includePerspectiveMatrix(value) {
        this._includePerspectiveMatrix = value === true;
    }

    get includePerspectiveMatrix() {
        return this._includePerspectiveMatrix === true;
    }

    set includeModelViewMatrix(value) {
        this._includeModelViewMatrix = value === true;
    }

    get includeModelViewMatrix() {
        return this._includeModelViewMatrix === true;
    }

    set textures(value) {
        if (value instanceof Array) {
            this._textures = value;
        }
    }

    get textures() {
        return this._textures || [];
    }

    set clearing(value) {
        this._clearing = value === true;
    }

    get clearing() {
        return this._clearing === true;
    }

    set running(value) {
        if (!this.running && value === true) {

            this._then = Date.now();

            requestAnimationFrame(this.run);
        }
        this._running = value === true;
    }

    get running() {
        return this._running === true;
    }

    set frameRate(value) {
        if (!isNaN(value)) this._frameRate = 1000 / value;
    }

    get frameRate() {
        return this._frameRate || null;
    }

    set pxratio(value) {
        if (value > 0) this._pxratio = value;
    }

    get pxratio() {
        return this._pxratio || 1;
    }

    get perspectiveMatrix() {
        const fieldOfView = 45 * Math.PI / 180; // in radians
        const aspect = this._size.w / this._size.h;
        const zNear = 0.1;
        const zFar = 100.0;
        const projectionMatrix = mat4.create();
        mat4.perspective(projectionMatrix,
            fieldOfView,
            aspect,
            zNear,
            zFar);

        return projectionMatrix;
    }

    get modelViewMatrix() {
        const modelViewMatrix = mat4.create();

        mat4.translate(modelViewMatrix, // destination matrix
            modelViewMatrix, // matrix to translate
            [-0.0, 0.0, -1.]); // amount to translate

        return modelViewMatrix;
    }

    set onRun(runMethod) {
        if (typeof runMethod == 'function') {
            this._onRun = runMethod.bind(this);
        }
    }

    get onRun() {
        return this._onRun;
    }

    get context() {
        return this._ctx || null;
    }

    /**
     * Static Methods
     */

    static createShaderOfType(ctx, type, source) {
        const shader = ctx.createShader(type);
        ctx.shaderSource(shader, source);
        ctx.compileShader(shader);

        if (!ctx.getShaderParameter(shader, ctx.COMPILE_STATUS)) {
            console.log('An error occurred compiling the shaders: ' + ctx.getShaderInfoLog(shader));
            ctx.deleteShader(shader);
            return null;
        }

        return shader;
    }
}

WTCGL.TYPE_INT = 0;
WTCGL.TYPE_FLOAT = 1;
WTCGL.TYPE_V2 = 2;
WTCGL.TYPE_V3 = 3;
WTCGL.TYPE_V4 = 4;
WTCGL.TYPE_BOOL = 5;

WTCGL.IMAGETYPE_REGULAR = 0;
WTCGL.IMAGETYPE_TILE = 1;
WTCGL.IMAGETYPE_MIRROR = 2;

WTCGL.TEXTYPE_FLOAT = 0;
WTCGL.TEXTYPE_UNSIGNED_BYTE = 1;
WTCGL.TEXTYPE_HALF_FLOAT_OES = 2;

/**
 * MoleculeRenderer - Библиотека для отрисовки анимированной 3D молекулы с использованием WebGL.
 * Базируется на классе WTCGL.
 *
 * @class MoleculeRenderer
 */
class MoleculeRenderer {
    /**
     * Создает экземпляр MoleculeRenderer.
     *
     * @param {HTMLCanvasElement} canvas - Элемент canvas для отрисовки.
     * @param {Object} options - Параметры инициализации.
     * @param {number} [options.width=800] - Ширина канваса.
     * @param {number} [options.height=600] - Высота канваса.
     * @param {number} [options.pxratio] - Pixel ratio (по умолчанию рассчитывается автоматически).
     * @param {Object} [options.params] - Начальные параметры отрисовки.
     */
    constructor(canvas, options = {}) {
        this.canvas = canvas;
        this.width = options.width || 800;
        this.height = options.height || 600;
        this.pxratio = options.pxratio || Math.min(window.devicePixelRatio, 1.5);

        this.isPaused = false;
        this.isVisible = true;
        this.rotationAngle = 0;
        this.rotationSpeed = options.rotationSpeed !== undefined ? options.rotationSpeed : 0.01;
        this.accumulatedTime = 0;

        this._initWTCGL();

        // Установка начальных параметров
        const defaultParams = {
            GLOW_LEVEL: 4.5,
            MARGIN_CSS: 50,
            DISTORTION_STRENGTH: 0.15,
            NOISE_SCALE: 3.0,
            TIME_SPEED: 1.2,
            ROTATION_SPEED: 0.5,
            ZOOM: 1.0,
            CONTOUR_FREQUENCY: 15,
            CONTOUR_THICKNESS: 0.05,
            CONTOUR_BRIGHTNESS: 0.8,
            CONTOUR_FLOW_SPEED: 0.5,
            COLOR_BASE: [0.0, 0.3, 1.0], // #004dff
            COLOR_GLOW: [0.4, 0.7, 1.0]  // #66b3ff
        };

        const initialParams = Object.assign({}, defaultParams, options.params || {});
        this._setParameters(initialParams);

        this._initIntersectionObserver();

        this._start();
    }

    /**
     * @private
     */
    _initIntersectionObserver() {
        if (!('IntersectionObserver' in window)) return;

        this._observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                this.isVisible = entry.isIntersecting;
            });
        }, {threshold: 0});

        this._observer.observe(this.canvas);
    }

    /**
     * @private
     */
    _initWTCGL() {
        const vertexSource = `
            attribute vec4 a_position;
            void main() {
                gl_Position = a_position;
            }
        `;

        const fragmentSource = `
            precision highp float;
            uniform vec2 u_resolution;
            uniform float u_time;
            uniform float u_pxratio;

            uniform float u_GLOW_LEVEL;
            uniform float u_MARGIN_CSS;
            uniform vec3 u_COLOR_BASE;
            uniform vec3 u_COLOR_GLOW;

            uniform float u_DISTORTION_STRENGTH;
            uniform float u_NOISE_SCALE;
            uniform float u_TIME_SPEED;
            uniform float u_ZOOM;

            uniform float u_CONTOUR_FREQUENCY;
            uniform float u_CONTOUR_THICKNESS;
            uniform float u_CONTOUR_BRIGHTNESS;
            uniform float u_CONTOUR_FLOW_SPEED;
            uniform float u_rotation;
            uniform float u_ROTATION_SPEED;

            float hash(float n) { return fract(sin(mod(n, 32768.0)) * 43758.5453123); }
            float noise(vec3 x) {
                vec3 p = floor(x);
                vec3 f = fract(x);
                f = f*f*(3.0-2.0*f);
                float n = p.x + p.y*57.0 + 113.0*p.z;
                return mix(mix(mix( hash(n+  0.0), hash(n+  1.0),f.x),
                               mix( hash(n+ 57.0), hash(n+ 58.0),f.x),f.y),
                           mix(mix( hash(n+113.0), hash(n+114.0),f.x),
                               mix( hash(n+170.0), hash(n+171.0),f.x),f.y),f.z);
            }

            float getScalarField(vec3 p) {
                float n = noise(p * u_NOISE_SCALE + u_time * u_TIME_SPEED);
                return n * u_DISTORTION_STRENGTH;
            }

            float map(vec3 p, float baseRadius) {
                float d = length(p) - baseRadius;
                d -= getScalarField(p);
                return d;
            }

            vec3 getNormal(vec3 p, float baseRadius) {
                vec2 e = vec2(0.01, 0.0);
                return normalize(vec3(
                    map(p + e.xyy, baseRadius) - map(p - e.xyy, baseRadius),
                    map(p + e.yxy, baseRadius) - map(p - e.yxy, baseRadius),
                    map(p + e.yyx, baseRadius) - map(p - e.yyx, baseRadius)
                ));
            }

            void main() {
                vec2 uv = (gl_FragCoord.xy - 0.5 * u_resolution.xy) / min(u_resolution.y, u_resolution.x);
                vec3 ro = vec3(0.0, 0.0, 2.0);
                vec3 rd = normalize(vec3(uv, -1.5 * u_ZOOM));

                float s = sin(u_rotation);
                float c = cos(u_rotation);
                mat2 rot = mat2(c, -s, s, c);
                ro.xz *= rot;
                rd.xz *= rot;

                float min_res_css = min(u_resolution.x, u_resolution.y) / u_pxratio;
                float uv_margin = max(0.0, 0.5 - u_MARGIN_CSS / min_res_css);
                float baseRadius = 2.0 * uv_margin / sqrt(uv_margin * uv_margin + 2.25);

                float t = 0.0;
                bool hit = false;
                float glow = 0.0;

                for(int i = 0; i < 40; i++) {
                    vec3 p = ro + rd * t;
                    float d = map(p, baseRadius);
                    glow += 0.01 / (0.01 + abs(d));
                    if(d < 0.002) {
                        hit = true;
                        break;
                    }
                    t += d;
                    if(t > 3.5) break;
                }

                vec3 color = vec3(0.0);
                float alpha = 0.0;

                if (hit) {
                    vec3 p = ro + rd * t;
                    vec3 norm = getNormal(p, baseRadius);
                    vec3 lightDir = normalize(vec3(1.0, 1.0, 2.0));
                    float diff = max(dot(norm, lightDir), 0.0);
                    float centerDist = length(p);
                    float innerGlow = pow(1.0 - (centerDist / (baseRadius + u_DISTORTION_STRENGTH)), 2.0);
                    innerGlow = clamp(innerGlow, 0.0, 1.0);

                    color = mix(u_COLOR_BASE, u_COLOR_GLOW, innerGlow * 0.8 * u_GLOW_LEVEL);
                    color += u_COLOR_GLOW * diff * 0.3;

                    float fieldVal = getScalarField(p) / u_DISTORTION_STRENGTH;
                    float lineValue = fract(fieldVal * u_CONTOUR_FREQUENCY + u_time * u_CONTOUR_FLOW_SPEED);
                    float lineMask = smoothstep(u_CONTOUR_THICKNESS, 0.0, abs(lineValue - 0.5));
                    color += u_COLOR_GLOW * lineMask * u_CONTOUR_BRIGHTNESS;
                    color += u_COLOR_GLOW * glow * 0.02 * u_GLOW_LEVEL;
                    alpha = 1.0;
                } else {
                    color = u_COLOR_BASE * glow * 0.015 * u_GLOW_LEVEL;
                    alpha = clamp(max(color.r, max(color.g, color.b)), 0.0, 1.0);
                }
                gl_FragColor = vec4(color, alpha);
            }
        `;

        this.wtcgl = new WTCGL(
            this.canvas,
            vertexSource,
            fragmentSource,
            this.width,
            this.height,
            this.pxratio
        );
        this.wtcgl.clearing = true;
        this.wtcgl.addUniform('pxratio', WTCGL.TYPE_FLOAT, this.pxratio);
    }

    /**
     * @private
     */
    _start() {
        this.wtcgl.onRun = (delta) => {
            if (this.isPaused || !this.isVisible) return;

            this.accumulatedTime += 0.016;
            if (this.accumulatedTime > 3600.0) this.accumulatedTime -= 3600.0; // Сброс каждый час для предотвращения потери точности в шейдерах

            // Автоматическое вращение
            const speed = this.wtcgl.getUniform ? (this.wtcgl.getUniform('ROTATION_SPEED') || 0.0) : 0.0;
            this.rotationAngle += 0.01 * speed;
            const TWO_PI = Math.PI * 2;
            if (this.rotationAngle > TWO_PI) this.rotationAngle %= TWO_PI;

            this.wtcgl.time = this.accumulatedTime;
            this.wtcgl.addUniform('rotation', WTCGL.TYPE_FLOAT, this.rotationAngle);

            if (this._onFrameCallback) {
                this._onFrameCallback();
            }
        };
        this.wtcgl.running = true;
    }

    /**
     * Вспомогательная функция для преобразования HEX в vec3.
     * @param {string} hex - Цвет в формате #RRGGBB.
     * @returns {number[]} Массив из 3-х компонент [r, g, b].
     */
    static hexToVec3(hex) {
        const r = parseInt(hex.slice(1, 3), 16) / 255;
        const g = parseInt(hex.slice(3, 5), 16) / 255;
        const b = parseInt(hex.slice(5, 7), 16) / 255;
        return [r, g, b];
    }

    /**
     * @private
     */
    _setParameters(params) {
        for (let key in params) {
            let val = params[key];
            let type = WTCGL.TYPE_FLOAT;

            // Если передана строка HEX цвета, конвертируем в vec3
            if (typeof val === 'string' && val.startsWith('#')) {
                val = MoleculeRenderer.hexToVec3(val);
            }

            if (Array.isArray(val)) {
                if (val.length === 2) type = WTCGL.TYPE_V2;
                else if (val.length === 3) type = WTCGL.TYPE_V3;
                else if (val.length === 4) type = WTCGL.TYPE_V4;
            }

            this.wtcgl.addUniform(key, type, val);
        }
    }

    /**
     * Устанавливает параметры отрисовки.
     *
     * @param {Object} params - Объект с параметрами (GLOW_LEVEL, COLOR_BASE и т.д.).
     */
    setParameters(params) {
        this._setParameters(params);
    }

    /**
     * Get a uniform value.
     */
    getUniform(name) {
        return this._uniformValues ? this._uniformValues[name] : null;
    }

    /**
     * Изменяет размер области отрисовки.
     *
     * @param {number} width - Новая ширина.
     * @param {number} height - Новая высота.
     */
    resize(width, height) {
        this.width = width;
        this.height = height;
        this.wtcgl.resize(width, height);
    }

    /**
     * Останавливает или запускает анимацию.
     *
     * @param {boolean} pause - true для паузы, false для запуска.
     */
    setPaused(pause) {
        this.isPaused = pause;
    }

    /**
     * Выполняет вращение на 360 градусов.
     *
     * @param {number} duration - Длительность вращения в мс (по умолчанию ~2 секунды при шаге 0.05).
     */
    rotate360() {
        if (this._isRotating360) return;
        this._isRotating360 = true;
        const prevPaused = this.isPaused;
        this.isPaused = true;

        const targetRotation = this.rotationAngle + Math.PI * 2;
        const step = 0.05;

        const animate = () => {
            if (this.rotationAngle < targetRotation) {
                this.rotationAngle += step;
                if (this.rotationAngle > targetRotation) this.rotationAngle = targetRotation;
                this.wtcgl.addUniform('rotation', WTCGL.TYPE_FLOAT, this.rotationAngle);
                requestAnimationFrame(animate);
            } else {
                this._isRotating360 = false;
                this.isPaused = prevPaused;
            }
        };
        animate();
    }

    /**
     * Возвращает контекст WebGL.
     * @returns {WebGLRenderingContext}
     */
    getGLContext() {
        return this.wtcgl.context;
    }

    /**
     * Устанавливает callback, вызываемый на каждом кадре.
     * @param {Function} callback
     */
    onFrame(callback) {
        this._onFrameCallback = callback;
    }

    /**
     * Статический метод для упрощенной инициализации.
     * @param {HTMLCanvasElement} canvas
     * @param {Object} options
     * @returns {MoleculeRenderer}
     */
    static render(canvas, options = {}) {
        return new MoleculeRenderer(canvas, options);
    }
}

// Экспорт в глобальное пространство имен для Rails и обычных тегов <script>
if (typeof window !== 'undefined') {
    window.MoleculeRenderer = MoleculeRenderer;
} else if (typeof globalThis !== 'undefined') {
    globalThis.MoleculeRenderer = MoleculeRenderer;
}

// Поддержка CommonJS
if (typeof module !== 'undefined' && module.exports) {
    module.exports = MoleculeRenderer;
}
